import { Functor } from 'functions/Functor';
import * as OrderGuideList from 'types/Ingredient/Detail/OrderGuide/OrderGuideList';
import * as Product from 'types/Ingredient/Detail/OrderGuide/Product';
import * as ProductList from 'types/Ingredient/Detail/OrderGuide/ProductList';

export type Type = {
	searchedProducts: ProductList.Type;
	orderGuides: OrderGuideList.Type;
	isProductSearchOpen: boolean;
};

export function create(): Type {
	return {
		searchedProducts: [],
		orderGuides: OrderGuideList.create(),
		isProductSearchOpen: false,
	};
}

export async function initialize(obj?: Type, ingredientId?: string): Promise<Type> {
	if (!obj) obj = create();
	if (!ingredientId) return obj;
	const output = {
		...obj,
		orderGuides: await OrderGuideList.getFromApiByIngredientId(ingredientId),
		searchedProducts: await searchByIngredientId(ingredientId),
	};
	return output;
}

export function hasProducts(obj: Type): boolean {
	for (const orderGuide of obj.orderGuides) {
		if (orderGuide.products.length > 0) return true;
	}
	return false;
}

async function searchByIngredientId(ingredientId: string): Promise<ProductList.Type> {
	return Functor.From([ingredientId])
		.mapAsync((e) =>
			ProductList.getFromApiSggestions(e, { type: 'id', amountOfSuggestions: 3 })
		)
		.map(ProductList.mapFromApiSuggestions)
		.map((e) => ProductList.enrichWithIngredientId(e, ingredientId))
		.get();
}

async function searchBySearchTerm(input: string, ingredientId: string): Promise<ProductList.Type> {
	return Functor.From([input])
		.mapAsync((e) =>
			ProductList.getFromApiSggestions(e, { type: 'name', amountOfSuggestions: 3 })
		)
		.map(ProductList.mapFromApiSuggestions)
		.map((e) => ProductList.enrichWithIngredientId(e, ingredientId))
		.get();
}

export async function searchProducts(
	obj?: Type,
	input?: {
		ingredientId: string;
		searchTerm?: string;
		type: 'name' | 'id';
	}
): Promise<Type> {
	if (!obj) return create();
	if (!input) return obj;
	if (input.type === 'id') {
		return {
			...obj,
			searchedProducts: await searchByIngredientId(input.ingredientId),
		};
	}
	if (!input.searchTerm) return obj;
	return {
		...obj,
		searchedProducts: await searchBySearchTerm(input.searchTerm, input.ingredientId),
	};
}

export function openProductSearch(obj: Type): Type {
	return {
		...obj,
		isProductSearchOpen: true,
	};
}

export function closeProductSearch(obj: Type): Type {
	return {
		...obj,
		isProductSearchOpen: false,
	};
}

export async function removeProduct(
	obj?: Type,
	input?: {
		orderGuideId: string;
		productId: string;
	}
): Promise<Type> {
	if (!obj) return create();
	if (!input) return obj;
	return {
		...obj,
		orderGuides: await OrderGuideList.removeProduct(
			obj.orderGuides,
			input.orderGuideId,
			input.productId
		),
	};
}

export async function addProduct(
	obj?: Type,
	input?: {
		product: Product.Type;
	}
): Promise<Type> {
	if (!obj) return create();
	if (!input) return obj;
	return {
		...obj,
		orderGuides: await OrderGuideList.addProduct(obj.orderGuides, input.product),
		isProductSearchOpen: false,
	};
}

export function togglePriority(
	obj: Type,
	input: {
		productId: string;
		sourceId: string;
		priority: number;
	}
): Type {
	return {
		...obj,
		searchedProducts: obj.searchedProducts.map((e) => {
			if (e.productId !== input.productId) return e;
			if (e.sourceId !== input.sourceId) return e;
			return Product.togglePriority(e, input.priority);
		}),
	};
}

export async function changePriorityOfProduct(
	obj?: Type,
	input?: {
		orderGuideId: string;
		productId: string;
		sourceId: string;
		priority: number;
	}
): Promise<Type> {
	if (!obj) return create();
	if (!input) return obj;
	return {
		...obj,
		orderGuides: await OrderGuideList.changePriority(obj.orderGuides, input),
	};
}
